/** @format */

import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import {
	HelpCircle,
	Search,
	BookOpen, // Main Icons
	Ruler,
	Palette,
	Wrench,
	Sparkles, // Planning & Installation Icons
	Truck,
	ShieldCheck,
	CornerDownLeft,
	Lock,
	FileText, // Policy Icons
} from "lucide-react"; // Import specific icons

// Components
import ContactBlock from "../../components/contact/ContactBlock"; // Assuming path

// Data structure for guides, categorized and using Lucide icons
const guideCategories = [
	{
		categoryTitle: "Before You Order",
		items: [
			{
				name: "Choosing Your Fabric",
				icon: Palette,
				link: "/guides/productselection",
				description: "Explore materials, colors, and light control options.",
			},
			{
				name: "How to Measure Accurately",
				icon: Ruler,
				link: "/guides/measuring-guide",
				description: "Simple steps for inside and outside mounts.",
			},
		],
	},
	{
		categoryTitle: "After You Order",
		items: [
			{
				name: "Installation Instructions",
				icon: Wrench,
				link: "/guides/installation-guide",
				description: "Step-by-step guides for easy setup.",
			},
			{
				name: "Shipping Information",
				icon: Truck,
				link: "/help/shipping-policy",
				description: "Delivery times, costs, and tracking info.",
			},
			// { name: "Cleaning & Care", icon: Sparkles, link: "/guides/care", description: "Keep your shades looking their best." }, // Add if available
		],
	},
	{
		categoryTitle: "Policies & Warranty",
		items: [
			{
				name: "Our Guarantee & Warranty",
				icon: ShieldCheck,
				link: "/help/warranty",
				description: "Understand your product coverage.",
			}, // Combined Warranty/Guarantee?
			{
				name: "Return Policy",
				icon: CornerDownLeft,
				link: "/help/return-policy",
				description: "Our hassle-free returns process.",
			},
			{
				name: "Privacy Policy",
				icon: Lock,
				link: "/help/privacy-policy",
				description: "How we protect your data.",
			},
			{
				name: "Terms & Conditions",
				icon: FileText,
				link: "/help/terms-conditions",
				description: "Our service terms.",
			},
		],
	},
];

// --- Reusable Guide Card Component ---
const GuideCard = ({ item, delay }) => {
	const IconComponent = item.icon || HelpCircle; // Fallback icon
	return (
		<motion.div
			initial={{ opacity: 0, y: 20 }}
			whileInView={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.4, delay: delay, ease: "easeOut" }}
			viewport={{ once: true, amount: 0.2 }}
		>
			<Link to={item.link} className="block h-full group">
				<motion.div
					className="bg-white rounded-xl h-full p-6 border border-gray-200 shadow-sm hover:shadow-lg hover:border-primary/50 transition-all duration-200 flex flex-col" // Use flex-col for structure
					whileHover={{ y: -5 }}
				>
					<div className="flex items-center gap-3 mb-3">
						<div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
							<IconComponent size={24} strokeWidth={1.5} />
						</div>
						<h2 className="text-lg font-semibold text-gray-800 group-hover:text-primary transition-colors">
							{item.name}
						</h2>
					</div>
					<p className="text-sm text-gray-600 flex-grow">
						{" "}
						{/* flex-grow pushes link to bottom */}
						{item.description}
					</p>
					{/* Optional: Add an explicit "Read More" link */}
					{/* <span className="mt-4 text-sm font-medium text-primary group-hover:underline">Read Guide →</span> */}
				</motion.div>
			</Link>
		</motion.div>
	);
};

// --- Main Guides Screen ---
const GuideScreen = () => {
	return (
		<div className="overflow-x-hidden bg-gray-50">
			<Helmet>
				<title>Guides & Support Center | Off Cut Shades</title>{" "}
				{/* Updated Title */}
				<meta
					name="description"
					content="Find help with choosing fabrics, measuring windows, installation, shipping, returns, warranty, and more. Get support from Off Cut Shades."
				/>{" "}
				{/* Updated Desc */}
				<link rel="canonical" href="https://offcutshades.com/guides" />{" "}
				{/* Updated Canonical */}
			</Helmet>

			<main>
				{/* --- Hero Section --- */}
				<section className="bg-gradient-to-b from-white to-gray-50 pt-24 pb-16 md:pt-32 md:pb-20 text-center">
					<div className="container mx-auto px-4 max-w-3xl">
						<motion.div
							initial={{ scale: 0.8, opacity: 0 }}
							animate={{ scale: 1, opacity: 1 }}
							transition={{ type: "spring", stiffness: 150, delay: 0.1 }}
							className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 text-primary mb-4"
						>
							<BookOpen size={36} strokeWidth={1.5} />
						</motion.div>
						<motion.h1
							initial={{ opacity: 0, y: 10 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.5, delay: 0.2 }}
							className="text-4xl md:text-5xl font-bold text-gray-900 mb-4"
						>
							Guides & Support Center
						</motion.h1>
						<motion.p
							initial={{ opacity: 0, y: 10 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.5, delay: 0.3 }}
							className="text-lg text-gray-600 max-w-2xl mx-auto"
						>
							Your resource for choosing, measuring, installing, and caring for
							your custom shades. Find answers and helpful information below.
						</motion.p>
						{/* Optional: Search Bar */}
						{/* <motion.div
                             initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.4 }}
                             className="mt-8 max-w-lg mx-auto relative" >
                             <input type="search" placeholder="Search guides & FAQs..." className="w-full pl-10 pr-4 py-3 rounded-full border border-gray-300 focus:ring-primary focus:border-primary"/>
                             <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"/>
                         </motion.div> */}
					</div>
				</section>

				{/* --- Guides Sections --- */}
				<section className="container mx-auto px-4 py-12 md:py-16">
					{guideCategories.map((category, catIndex) => (
						<div key={category.categoryTitle} className="mb-10 md:mb-14">
							{/* Category Title */}
							<motion.h2
								initial={{ opacity: 0, x: -20 }}
								whileInView={{ opacity: 1, x: 0 }}
								viewport={{ once: true, amount: 0.3 }}
								transition={{ duration: 0.4, delay: 0.1 }}
								className="text-2xl md:text-3xl font-semibold text-gray-800 mb-6 md:mb-8 pb-2 border-b border-gray-200"
							>
								{category.categoryTitle}
							</motion.h2>
							{/* Grid for items in this category */}
							<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
								{category.items.map((item, index) => (
									<GuideCard
										key={item.name}
										item={item}
										// Stagger delay within each category grid
										delay={index * 0.08 + catIndex * 0.1} // Add small base delay per category
									/>
								))}
							</div>
						</div>
					))}
					{/* Link to General FAQ */}
					<div className="text-center mt-12">
						<p className="text-gray-600 mb-4">
							Can't find what you're looking for?
						</p>
						<Link to="/help/faq" className="btn btn-secondary-outline">
							{" "}
							{/* Example button style */}
							View Frequently Asked Questions
						</Link>
					</div>
				</section>

				{/* --- Contact Support Section --- */}
				{/* Optionally pass a more specific title to ContactBlock */}
				<ContactBlock
					title="Still Need Assistance?"
					description="Our support team is ready to help with any questions you have."
				/>
			</main>
		</div>
	);
};

// Add button styles if not global
/*
@layer components {
  .btn { @apply px-5 py-2.5 rounded-lg font-semibold text-center shadow-sm transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed; }
  .btn-primary { @apply bg-primary text-white hover:bg-primary/90; }
  .btn-secondary-outline { @apply bg-white text-gray-700 border-2 border-gray-300 hover:border-gray-400 hover:bg-gray-50; }
}
*/

export default GuideScreen;
